// ── Overview tab — the cohort summary (Wave 3, REAL read) ───────────────────
// Reuses the already-loaded cohort detail (passed down from the orchestrator so
// the header + this tab share one read) and surfaces program / company / coach /
// status. The LIVE counts come from the SAME participants / sessions reads the
// Participants / Sessions tabs use (shared react-query keys → deduped, cached) —
// these are LIVE counts, never the dead denorm column (blueprint §1). A count
// that hasn't loaded (or that errored) reads as "—", never a fabricated 0.
// READ-ONLY: the write flows (edit dropdowns, complete) live on the list /
// orchestrator, not here.

import { Badge, Card } from "@vjal/ui";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type {
  CoachingCohortDto,
  CoachingParticipantDto,
  CoachingSessionDto,
  ListResponse
} from "../../../services/coaching-dtos";
import { cohortStatusLabel, cohortStatusTone, orDash } from "../cohort-detail-helpers";
import type { CoachingRole } from "../../../domain/roles";
import { CohortAdminActionsPanel } from "../../product-z6-cohort-admin-actions";

const services = getCoachingServices();

export function CohortOverviewTab({ cohort, role }: { cohort: CoachingCohortDto; role: CoachingRole }) {
  // Shared keys with the Participants / Sessions tabs → cached, no double fetch.
  // A non-ready posture leaves the count null (rendered "—"), never a fake 0.
  const participantsQuery = useHonestQuery<ListResponse<CoachingParticipantDto>>(
    coachingQueryKeys.list("participants.listForCohort", { cohort_id: cohort.id }),
    () => services.participants.listForCohort(cohort.id)
  );
  const sessionsQuery = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohort.id }),
    () => services.sessions.listForCohort(cohort.id)
  );

  const participantCount = countFrom(participantsQuery.data, participantsQuery.posture);
  const sessionCount = countFrom(sessionsQuery.data, sessionsQuery.posture);

  return (
    <div className="space-y-4" data-testid="cohort-overview-tab">
      <Card className="shadow-none">
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <Field label="Status">
            <Badge variant={cohortStatusTone(cohort.status)}>{cohortStatusLabel(cohort.status)}</Badge>
          </Field>
          <Field label="Reference">{cohort.id}</Field>
          <Field label="Company">{orDash(cohort.company_name)}</Field>
          <Field label="Program">{orDash(cohort.program_name)}</Field>
          <Field label="Coach">{orDash(cohort.coach_name)}</Field>
          <Field label="Participants">{participantCount === null ? "—" : String(participantCount)}</Field>
          <Field label="Sessions">{sessionCount === null ? "—" : String(sessionCount)}</Field>
        </div>
      </Card>
      <CohortAdminActionsPanel cohortId={cohort.id} role={role} />
    </div>
  );
}

/** Count is the LIVE list length on a ready/empty read; otherwise unknown. */
function countFrom<T>(data: ListResponse<T> | undefined, posture: string | null): number | null {
  if (posture !== null && posture !== "empty") return null;
  return data?.items?.length ?? null;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <p className="m-0 text-sm text-strong">{children}</p>
    </div>
  );
}
